import { useEffect } from 'react';
import urlTemplate from 'url-template';
import type {
  ContractManagementResponse,
  ContractsParams,
  ContractsResponse,
} from 'src/apis/contract-management/types';
import type { ContractManagementRequest } from 'src/apis/contract-management/use-contract-management';
import { useAxios } from 'src/hooks/use-axios';
import { Forbidden } from 'src/utils/errors';

type HookConfig = {
  contractManagement: ContractManagementRequest;
  params?: ContractsParams;
};

type ExecuteProps = {
  rootData: ContractManagementResponse;
  params?: ContractsParams;
};

export function useContracts({ contractManagement, params }: HookConfig) {
  const request = useAxios(
    (axios, baseConfig, { rootData, params }: ExecuteProps) => {
      if (!rootData._links.contracts) throw new Forbidden();

      const url = urlTemplate.parse(rootData._links.contracts.href).expand({});

      return axios.request<ContractsResponse>({
        ...baseConfig,
        url,
        params,
      });
    },
    { neededOnPageLoad: true }
  );

  useEffect(() => {
    if (!contractManagement.response) return;

    const rootData = contractManagement.response.data;
    const options = { rootData, params };

    request.execute(options);
    // oxlint-disable-next-line exhaustive-deps
  }, [contractManagement.response, params]);

  return request;
}

export type ContractsRequest = ReturnType<typeof useContracts>;
